import React, { useEffect, useState } from "react";
import { API_URL } from "../../environment";

interface Resposta {
  id: number;
  id_cliente: number;
  titulo: string;
  texto: string;
  id_user: number | null;
  created_at: string;
}

const RespostasRapidas: React.FC = () => {
  const [itens, setItens] = useState<Resposta[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [titulo, setTitulo] = useState("");
  const [texto, setTexto] = useState("");

  const id_cliente = localStorage.getItem("id_grupo") || ""; // por GRUPO
  const id_user = localStorage.getItem("id_user") || "";

  const fetchItens = async () => {
    if (!id_cliente) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/respostas_rapidas?id_cliente=${id_cliente}&id_user=${id_user}`);
      if (res.ok) setItens(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpar = () => {
    setEditId(null);
    setTitulo("");
    setTexto("");
  };

  const salvar = async () => {
    if (!titulo.trim() || !texto.trim()) {
      alert("Preencha o título e o texto.");
      return;
    }
    if (!id_cliente) {
      alert("Grupo não identificado. Refaça o login.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        const res = await fetch(`${API_URL}/respostas_rapidas/${editId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ titulo, texto, id_user }),
        });
        if (!res.ok) throw new Error();
      } else {
        const res = await fetch(`${API_URL}/respostas_rapidas`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_cliente, titulo, texto, id_user }),
        });
        if (!res.ok) throw new Error();
      }
      limpar();
      await fetchItens();
    } catch (e) {
      console.error(e);
      alert("Falha ao salvar a resposta rápida.");
    } finally {
      setSaving(false);
    }
  };

  const editar = (r: Resposta) => {
    setEditId(r.id);
    setTitulo(r.titulo);
    setTexto(r.texto);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const excluir = async (id: number) => {
    if (!window.confirm("Remover esta resposta rápida?")) return;
    try {
      const res = await fetch(`${API_URL}/respostas_rapidas/${id}?id_user=${id_user}`, { method: "DELETE" });
      if (res.ok) {
        setItens((prev) => prev.filter((r) => r.id !== id));
        if (editId === id) limpar();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content p-4" style={{ maxWidth: 900 }}>
        <div className="mb-4">
          <h4 className="mb-1 fw-bold d-flex align-items-center gap-2">
            <i className="ti ti-message-bolt text-primary" /> Respostas Rápidas
          </h4>
          <p className="text-muted mb-0 fs-13">Mensagens prontas do seu grupo. No chat, digite <b>/</b> para inserir uma delas.</p>
        </div>

        {/* Formulário de cadastro/edição */}
        <div className="card border shadow-sm mb-4">
          <div className="card-body">
            <h6 className="fw-bold mb-3">{editId ? "Editar resposta" : "Nova resposta"}</h6>
            <div className="mb-3">
              <label className="form-label fs-13 fw-medium">Título <span className="text-muted">(aparece no menu do /)</span></label>
              <input className="form-control" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex: Saudação inicial" maxLength={120} />
            </div>
            <div className="mb-3">
              <label className="form-label fs-13 fw-medium">Texto</label>
              <textarea className="form-control" rows={4} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ex: Olá! Tudo bem? Como posso te ajudar hoje?" />
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary rounded-pill px-4 fw-medium" disabled={saving} onClick={salvar}>
                {saving ? "Salvando..." : editId ? "Salvar alterações" : "Adicionar"}
              </button>
              {editId && (
                <button className="btn btn-light rounded-pill px-4" onClick={limpar}>Cancelar</button>
              )}
            </div>
          </div>
        </div>

        {/* Lista */}
        <h6 className="fw-bold mb-2">Cadastradas ({itens.length})</h6>
        {loading ? (
          <div className="text-center p-4"><div className="spinner-border text-primary" role="status" /></div>
        ) : itens.length === 0 ? (
          <div className="text-center p-4 border rounded-4 bg-light text-muted">Nenhuma resposta rápida cadastrada.</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {itens.map((r) => (
              <div key={r.id} className="card border shadow-sm">
                <div className="card-body p-3 d-flex align-items-start justify-content-between gap-3">
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-bold">{r.titulo}</div>
                    <div className="text-muted small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{r.texto}</div>
                  </div>
                  <div className="d-flex gap-1 flex-shrink-0">
                    <button className="btn btn-sm btn-outline-secondary border-0 p-1" title="Editar" onClick={() => editar(r)}>
                      <i className="ti ti-pencil" />
                    </button>
                    <button className="btn btn-sm btn-outline-danger border-0 p-1" title="Remover" onClick={() => excluir(r.id)}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RespostasRapidas;
